/**
 * Main application component — Dastarkhan Investigation Graph Platform
 *
 * When authenticated, displays the full application shell with navigation.
 * When not authenticated, shows sign-in UI.
 * Gracefully handles unconfigured backend (503 from /dastarkhan/).
 */

import React from 'react';
import { useAuth, useUser, useProviderLogout } from './auth/hooks';
import { getConfig } from './config';
import { EnvIndicator } from './components/EnvIndicator';
import { ApiStatus } from './components/ApiStatus';
import { logAuthEvent, getStoredCorrelationId, generateCorrelationId } from './auth/logging';
import { useDevModeConfig } from './hooks/useDevModeConfig.js';
import { DevModeSettings } from './components/DevModeSettings';
import { usePermissions } from './hooks/usePermissions';
import { DashboardPage } from './pages/DashboardPage';
import { ProjectPage } from './pages/ProjectPage';
import { AdminPage } from './pages/AdminPage';
import {
  Button,
  Card,
  Stack,
  Flex,
  BasicHeader,
  useTheme,
  spacing,
} from '@fti/design-system';
import type { MegaMenuNavigationItem } from '@fti/design-system';

type Page = 'dashboard' | 'project' | 'admin';

export const App: React.FC = () => {
  const auth = useAuth();
  const user = useUser();
  const config = getConfig();
  const { signoutRedirect } = useProviderLogout();
  const devModeConfig = useDevModeConfig();
  const { theme, setTheme } = useTheme();
  const permissions = usePermissions();

  const [currentPage, setCurrentPage] = React.useState<Page>('dashboard');
  const [selectedProjectId, setSelectedProjectId] = React.useState<string | null>(null);
  const [projectHeaderInfo, setProjectHeaderInfo] = React.useState<{ name: string; matterCode?: string } | null>(null);

  React.useEffect(() => {
    setTheme('light');
  }, [setTheme]);

  const effectiveApiUrl = devModeConfig.getEffectiveApiUrl();

  React.useEffect(() => {
    if (auth.error) {
      const correlationId = getStoredCorrelationId() || generateCorrelationId();
      logAuthEvent('auth_error', 'Authentication error occurred', {
        correlationId,
        errorType: auth.error.name,
        error: auth.error,
      });
    }
  }, [auth.error]);

  const handleSignIn = () => {
    // Clear the ?signed_out param so AuthGate resumes normal behavior
    window.history.replaceState({}, document.title, window.location.pathname);
    const correlationId = getStoredCorrelationId() || generateCorrelationId();
    logAuthEvent('auth_attempt', 'User initiated sign-in flow', { correlationId });
    auth.signinRedirect();
  };

  const navigateToProject = (projectId: string) => {
    setSelectedProjectId(projectId);
    setCurrentPage('project');
  };

  const navigateToDashboard = () => {
    setSelectedProjectId(null);
    setProjectHeaderInfo(null);
    setCurrentPage('dashboard');
  };

  const bgColor = theme.colors.background.default;
  const textColor = theme.colors.text.primary;
  const errorColor = theme.colors.semantic.error.default;

  // Unauthenticated view — sign in
  if (!auth.isAuthenticated) {
    return (
      <main
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: bgColor,
          color: textColor,
          padding: spacing.lg,
        }}
      >
        <Card variant="elevated" style={{ maxWidth: '480px', width: '100%' }}>
          <Card.Body>
            <Stack gap="md">
              <Flex justify="between" align="center">
                <h1 style={{ fontSize: '1.4rem', fontWeight: 600, margin: 0, color: textColor }}>
                  Dastarkhan
                </h1>
                <Flex align="center" gap="sm">
                  <DevModeSettings />
                  <EnvIndicator
                    environmentName={config.name}
                    displayName={config.displayName}
                    isMock={config.isMock}
                    apiTarget={devModeConfig.isDevModeAvailable ? devModeConfig.config.apiTarget : undefined}
                  />
                </Flex>
              </Flex>
              <p style={{ margin: 0, fontSize: '0.95rem', color: textColor }}>
                {auth.isLoading ? 'Checking session...' : 'Sign in to continue'}
              </p>
              {!auth.isLoading && (
                <Button variant="solid" color="primary" size="sm" onClick={handleSignIn}>
                  Sign in
                </Button>
              )}
              {auth.error && (
                <div
                  style={{
                    fontSize: '0.8rem',
                    color: errorColor,
                    background: `${theme.colors.semantic.error.light}20`,
                    border: `1px solid ${theme.colors.semantic.error.light}`,
                    borderRadius: '0.45rem',
                    padding: spacing.sm,
                  }}
                >
                  {auth.error.message?.includes('redirect_uri')
                    ? 'OAuth callback URL not configured. Add http://localhost:3000 to Cognito.'
                    : 'Check environment configuration.'}
                </div>
              )}
              <ApiStatus apiUrl={effectiveApiUrl} autoRefreshMs={30000} />
            </Stack>
          </Card.Body>
        </Card>
      </main>
    );
  }

  // Authenticated view — full application shell
  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: bgColor,
        color: textColor,
      }}
    >
      {/* Top Navigation Bar — FTI Design System BasicHeader */}
      <BasicHeader
        logo={{ size: 'medium', variant: 'default', onClick: navigateToDashboard }}
        items={(() => {
          const navItems: MegaMenuNavigationItem[] = [
            {
              id: 'projects',
              label: 'Projects',
              active: currentPage === 'dashboard',
              onClick: navigateToDashboard,
            },
          ];
          if (permissions.isAdmin) {
            navItems.push({
              id: 'admin',
              label: 'Admin',
              active: currentPage === 'admin',
              onClick: () => setCurrentPage('admin'),
            });
          }
          if (projectHeaderInfo && currentPage === 'project') {
            const projectLabel = projectHeaderInfo.matterCode
              ? `${projectHeaderInfo.name} (${projectHeaderInfo.matterCode})`
              : projectHeaderInfo.name;
            navItems.push({
              id: 'current-project',
              label: projectLabel,
              active: true,
              disabled: true,
            });
          }
          return navItems;
        })()}
        actions={
          <Flex align="center" gap="sm">
            <span style={{ fontSize: '0.85rem', color: theme.colors.text.secondary }}>
              {user?.email}
            </span>
            <DevModeSettings />
            <EnvIndicator
              environmentName={config.name}
              displayName={config.displayName}
              isMock={config.isMock}
              apiTarget={devModeConfig.isDevModeAvailable ? devModeConfig.config.apiTarget : undefined}
            />
            <Button variant="outline" color="error" size="sm" onClick={() => signoutRedirect()}>
              Sign out
            </Button>
          </Flex>
        }
      />

      {/* Main Content */}
      <div style={{ flex: 1, padding: spacing.lg }}>
        {!permissions.isConfigured ? (
          <Card variant="elevated" style={{ maxWidth: '600px', margin: '0 auto' }}>
            <Card.Body>
              <Stack gap="md">
                <h2 style={{ margin: 0, fontSize: '1.1rem', color: textColor }}>
                  Module Not Configured
                </h2>
                <p style={{ margin: 0, color: theme.colors.text.secondary }}>
                  The Dastarkhan investigation module is not configured on this environment.
                  Contact your administrator to set up DASTARKHAN_DATABASE_URL.
                </p>
                <ApiStatus apiUrl={effectiveApiUrl} autoRefreshMs={30000} />
              </Stack>
            </Card.Body>
          </Card>
        ) : (
          <>
            {currentPage === 'dashboard' && (
              <DashboardPage onNavigateToProject={navigateToProject} />
            )}
            {currentPage === 'project' && selectedProjectId && (
              <ProjectPage projectId={selectedProjectId} onBack={navigateToDashboard} onProjectInfo={setProjectHeaderInfo} />
            )}
            {currentPage === 'admin' && permissions.isAdmin && <AdminPage />}
            {currentPage === 'admin' && !permissions.isAdmin && (
              <Card>
                <Card.Body>
                  <p>Access denied. Admin privileges required.</p>
                  <Button variant="outline" size="sm" onClick={navigateToDashboard}>
                    Back to Dashboard
                  </Button>
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </div>
    </main>
  );
};
